import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/client'
import { Loading } from '../components/ui'
import { NotificationsSEO } from '../hooks/useSEO'
import type { Notification } from '../types'

interface NotificationsResponse {
  notifications: Notification[]
  total: number
  unreadCount: number
  page: number
  limit: number
  totalPages: number
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      const data = await api.get<NotificationsResponse>('/notifications')
      setNotifications(data.notifications)
      setUnreadCount(data.unreadCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMarkRead = async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (err) {
      console.error('Failed to mark read', err)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all')
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (err) {
      console.error('Failed to mark all read', err)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'JOB_APPLICATION':
      case 'JOB_APPLICATION_ACCEPTED':
      case 'JOB_APPLICATION_REJECTED':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        )
      case 'SUBMISSION_RECEIVED':
      case 'SUBMISSION_APPROVED':
      case 'SUBMISSION_REJECTED':
      case 'REVISION_REQUESTED':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        )
      case 'PAYMENT_RECEIVED':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <text x="12" y="16" textAnchor="middle" fill="currentColor" fontSize="12" fontWeight="bold">$</text>
          </svg>
        )
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        )
    }
  }

  if (isLoading) return <Loading message="Loading notifications..." />

  return (
    <div>
      <NotificationsSEO />
      <div className="page-header">
        <div>
          <h1>Notifications</h1>
          <p className="text-secondary">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button className="btn btn-secondary" onClick={handleMarkAllRead}>
            Mark All Read
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>
          <h3>No notifications</h3>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="items-list">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="card-static"
              style={{
                backgroundColor: notification.read ? 'var(--bg-primary)' : 'rgba(0, 100, 210, 0.05)',
                cursor: notification.read ? 'default' : 'pointer',
              }}
              onClick={() => !notification.read && handleMarkRead(notification.id)}
            >
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ebay-blue)',
                  flexShrink: 0,
                }}>
                  {getNotificationIcon(notification.type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <strong>{notification.title}</strong>
                    <span className="text-secondary" style={{ fontSize: 12 }}>
                      {new Date(notification.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p style={{ margin: '0.5rem 0 0', color: 'var(--text-secondary)' }}>
                    {notification.message}
                  </p>
                  {typeof notification.data?.jobId === 'string' && (
                    <Link
                      to={`/jobs/${notification.data.jobId}`}
                      className="text-secondary"
                      style={{ fontSize: 13 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Job &rarr;
                    </Link>
                  )}
                </div>
                {!notification.read && (
                  <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'var(--ebay-blue)',
                    flexShrink: 0,
                  }} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
